import type {TimeZone} from 'timezones-list';
import {timezoneAbbreviations} from '@Components/event-wizard/timezone-content/timezone-content-abbreviations.types';

const cldrNameCache = new Map<string, string>();
const gmtOffsetCache = new Map<string, string>();
const displayLabelCache = new Map<string, string>();

export const getCLDRTimeZoneName = (tzCode: string): string => {
  const cached = cldrNameCache.get(tzCode);
  if (cached !== undefined) {
    return cached;
  }

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tzCode,
      timeZoneName: 'longGeneric',
    });

    const parts = formatter.formatToParts(new Date());
    const name = parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
    cldrNameCache.set(tzCode, name);
    return name;
  } catch {
    cldrNameCache.set(tzCode, '');
    return '';
  }
};

export const getCityFromTzCode = (tzCode: string): string => {
  return tzCode.split('/').pop()?.replace(/_/g, ' ') ?? '';
};

export const getGMTOffsetFromTimeZone = (timeZone: TimeZone): string => {
  const cached = gmtOffsetCache.get(timeZone.tzCode);
  if (cached !== undefined) {
    return cached;
  }

  const empty = '+0';

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone.tzCode,
      timeZoneName: 'longOffset',
    });

    const parts = formatter.formatToParts(new Date());
    const timeZoneName = parts.find((part) => part.type === 'timeZoneName')?.value;

    if (!timeZoneName) {
      gmtOffsetCache.set(timeZone.tzCode, empty);
      return empty;
    }

    if (timeZoneName === 'GMT' || timeZoneName === 'UTC') {
      gmtOffsetCache.set(timeZone.tzCode, empty);
      return empty;
    }

    const offset = formatGMTOffset(timeZoneName.replace('GMT', ''));
    gmtOffsetCache.set(timeZone.tzCode, offset);
    return offset;
  } catch {
    gmtOffsetCache.set(timeZone.tzCode, empty);
    return empty;
  }
};

export const getGMTLabel = (timeZone: TimeZone): string => {
  return `GMT${getGMTOffsetFromTimeZone(timeZone)}`;
};

export const getTimezoneDisplayLabel = (timeZone: TimeZone, showTimeOffset: boolean): string => {
  const cached = displayLabelCache.get(timeZone.tzCode);

  if (cached !== undefined) {
    return !showTimeOffset ? cached : `${cached} (${getGMTLabel(timeZone)})`;
  }

  const cldrName = getCLDRTimeZoneName(timeZone.tzCode);
  const city = getCityFromTzCode(timeZone.tzCode);

  let label = '';

  if (cldrName && city) {
    label = `${cldrName} - ${city}`;
  } else if (city) {
    label = city;
  }
  return !showTimeOffset ? label : `${label} (${getGMTLabel(timeZone)})`;
};

/**
 * Example: Asia - Karachi
 * Replaces "/" with " - "
 * Replaces "_" with " "
 * @param tzCode
 */
export const getTzCodeText = (tzCode: string): string => {
  return tzCode.replace(/[/_]/g, (match) => {
    return match === '/' ? ' - ' : ' ';
  });
};

export const getShortTzCodeText = (tzCode: string): string => {
  const abbreviation = timezoneAbbreviations[tzCode];
  return !abbreviation ? '' : abbreviation;
};

export const getGMTOffsetMinutesFromTimeZone = (timeZone: TimeZone): number => {
  const timeString = getGMTOffsetFromTimeZone(timeZone);
  const sign = timeString[0];
  const parts = timeString.slice(1).split(':').map(Number);
  const totalMinutes = parts[0] * 60 + (parts[1] ?? 0);
  return sign === '-' ? -totalMinutes : totalMinutes;
};

const formatGMTOffset = (offset: string): string => {
  const sign = offset[0];
  const [hoursStr, minutesStr] = offset.slice(1).split(':');
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);
  return minutes !== 0 ? `${sign}${hours}:${minutesStr}` : `${sign}${hours}`;
};
